/*
  main.js — Restova storefront engine (non-cart scope)

  Our own replacement for the previous remote vendor bundle. The ENTIRE
  cart system — the drawer element and its open/close triggers, the
  quantity stepper, the cart-note save, the optimistic engine and the PDP
  ATC flow — lives in the cart-core.js asset, loaded ABOVE this file in the
  head (defer scripts execute in document order). This file owns only the
  non-cart leftovers.

  Scope:
  - sticky ATC bar (show after scrolling past the main ATC),
  - bare definitions for the few markup-anchor elements that no other
    script defines (product-form, product-info),
  - window.pauseAllMedia.
  The gallery, zoom, and other long-tail features are owned by the theme's
  local per-feature asset scripts (media-gallery.js, product-modal.js, …) —
  never define their element names here.
*/
(function () {
  'use strict';
  if (!('customElements' in window)) return;

  function defineOnce(tag, ctor) {
    if (!customElements.get(tag)) {
      customElements.define(tag, ctor || class extends HTMLElement {});
    }
  }

  /* NOTE: sticky-header / static-header are NOT defined here — the
     theme's own header section ships them via its {% javascript %} tag
     (Shopify compiles it into compiled_assets/scripts.js, which registers
     both with an unguarded define). Defining them here makes that bundle
     throw. Same rule as the per-feature asset scripts below. */

  /* ── Sticky ATC bar ──────────────────────────────────────────────────
     The product_sticky-atc block stylesheet has NO hidden state for
     .sticky-atc (position:fixed, always
     on) — the hide/show was runtime inline style. With
     data-after-scroll="true" the bar stays hidden until the main ATC
     button (the non-sticky one) scrolls up out of view. data-scroll-btn
     mode turns the button into a scroll-to-element shortcut instead. */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.reserveFooterSpace();
      if (this.hasAttribute('data-scroll-btn')) {
        this.addEventListener('click', function (e) {
          var btn = e.target.closest('button');
          if (!btn) return;
          e.preventDefault();
          var dest = document.getElementById(self.getAttribute('data-scroll-destination'));
          if (dest) dest.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
      if (!this.hasAttribute('data-after-scroll')) {
        /* 'Always' mode (no after-scroll gate): the bar is permanently visible
           (the CSS hide transform is keyed on [data-after-scroll]). Match the
           original by floating the corner buttons up by the bar's height.
           The bar IS shown here, so the impression beacon must fire (audit
           2026-07-11: this early return previously skipped the beacon wiring
           entirely — one editor flip of display_when to 'always' would have
           zeroed the sticky-ATC take-rate denominator while sticky atc-clicks
           kept flowing). */
        this.setFloatingOffset(true);
        this.markShownOnLoad();
        return;
      }
      var target = document.querySelector('button.main-product-atc:not(.sticky-atc__add_button)');
      /* The bar is hidden by CSS `transform: translateY(100%)` keyed on
         .sticky-atc[data-after-scroll="true"] (product_sticky-atc block stylesheet), with a 0.15s
         transform transition. REVEAL by overriding that with an inline
         `transform: none` (inline wins) once the main in-page ATC has scrolled
         up out of view; clear the inline transform to re-hide. The attribute
         stays put so the CSS transition animates both directions.
         NOTE: toggling `display` here does NOTHING — the transform keeps the
         bar parked below the viewport regardless of display. That mismatch was
         why the bar never appeared after the de-vendor swap. */
      if (!target) {
        /* No main ATC to key off (renamed/absent): reveal permanently — and
           the permanently-shown bar counts as an impression too (same audit
           fix as 'always' mode above). */
        this.style.transform = 'none';
        this.setFloatingOffset(true);
        this.markShownOnLoad();
        return;
      }
      var shownState = null;
      var update = function () {
        var scrolledPast = target.getBoundingClientRect().bottom < 0;
        if (scrolledPast) self._fireShown();
        if (shownState === scrolledPast) return;
        shownState = scrolledPast;
        self.style.transform = scrolledPast ? 'none' : '';
        /* Keep floating corner buttons above the visible bar. */
        self.setFloatingOffset(scrolledPast);
      };
      if ('IntersectionObserver' in window) {
        this._stickyObserver = new IntersectionObserver(function (entries) {
          var entry = entries[0];
          if (!entry) return;
          var scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
          if (scrolledPast) self._fireShown();
          if (shownState === scrolledPast) return;
          shownState = scrolledPast;
          self.style.transform = scrolledPast ? 'none' : '';
          self.setFloatingOffset(scrolledPast);
        }, { threshold: 0 });
        this._stickyObserver.observe(target);
      } else {
        update();
        var frame = 0;
        this._stickyUpdate = function () {
          if (frame) return;
          frame = requestAnimationFrame(function () {
            frame = 0;
            update();
          });
        };
        this._stickyCancelFrame = function () {
          if (frame) cancelAnimationFrame(frame);
          frame = 0;
        };
        window.addEventListener('scroll', this._stickyUpdate, { passive: true });
      }
      this._stickyResize = function () {
        if (shownState) self.setFloatingOffset(true);
        if (self._stickyUpdate) self._stickyUpdate();
      };
      window.addEventListener('resize', this._stickyResize, { passive: true });
      this._stickyPageshow = function (e) {
        if (!e.persisted) return;
        self._shownBeaconFired = false;
        update();
      };
      window.addEventListener('pageshow', this._stickyPageshow);
    }
    /* Impression beacon for the permanently-visible branches ('always' mode /
       missing scroll target): fire once now, and once per bfcache return —
       matching the after-scroll branch's once-per-pageview + pageshow-re-arm
       semantics so sticky-atc-shown means the same thing in every mode. */
    markShownOnLoad() {
      var self = this;
      this._fireShown();
      this._stickyPageshow = function (e) {
        if (e.persisted) { self._shownBeaconFired = false; self._fireShown(); }
      };
      window.addEventListener('pageshow', this._stickyPageshow);
    }
    _fireShown() {
      if (this._shownBeaconFired) return;
      this._shownBeaconFired = true;
      if (typeof window.rb === 'function') window.rb('event', 'sticky-atc-shown');
    }
    setFloatingOffset(shown) {
      var px = shown ? (this.offsetHeight + 'px') : '0px';
      var btns = document.querySelectorAll('.floating-btn');
      for (var i = 0; i < btns.length; i++) {
        btns[i].style.setProperty('--sticky-atc-offset', px);
      }
    }
    /* Reserve space below the footer content (= the bar's height) so the fixed
       bar never covers footer content (e.g. the payment icons). Native appended
       a body spacer, but our <body> is a CSS grid — appending a new element gets
       auto-placed and does NOT extend the page; growing an EXISTING grid item
       does. So we pad the footer instead: the padding sits INSIDE the footer, so
       it blends with the footer background, and the ATC stays available
       everywhere (vs hiding the bar at the footer). rAF-retries until the bar is
       laid out so the measured height is correct; only ever GROWS the padding,
       never shrinks the merchant's. The footer is far below the initial
       viewport, so defer this geometry work until it is within 1200px. */
    reserveFooterSpace() {
      var self = this, tries = 0;
      var footer = document.querySelector('.footer');
      if (!footer) return;
      function apply() {
        if (!self.isConnected) return;
        var h = self.offsetHeight;
        if (!h) { if (++tries < 60) requestAnimationFrame(apply); return; }
        var need = h + 8;
        var cur = parseFloat(window.getComputedStyle(footer).paddingBottom) || 0;
        if (cur < need) footer.style.paddingBottom = need + 'px';
      }
      if ('IntersectionObserver' in window) {
        var footerObserver = new IntersectionObserver(function (entries) {
          if (self._stickyFooterObserver !== footerObserver) return;
          if (!entries[0] || !entries[0].isIntersecting) return;
          footerObserver.disconnect();
          self._stickyFooterObserver = null;
          requestAnimationFrame(apply);
        }, { rootMargin: '1200px 0px' });
        this._stickyFooterObserver = footerObserver;
        footerObserver.observe(footer);
        return;
      }
      requestAnimationFrame(apply);
    }
    disconnectedCallback() {
      if (this._stickyUpdate) {
        window.removeEventListener('scroll', this._stickyUpdate);
        this._stickyUpdate = null;
      }
      if (this._stickyCancelFrame) {
        this._stickyCancelFrame();
        this._stickyCancelFrame = null;
      }
      if (this._stickyObserver) {
        this._stickyObserver.disconnect();
        this._stickyObserver = null;
      }
      if (this._stickyFooterObserver) {
        this._stickyFooterObserver.disconnect();
        this._stickyFooterObserver = null;
      }
      if (this._stickyResize) {
        window.removeEventListener('resize', this._stickyResize);
        this._stickyResize = null;
      }
      if (this._stickyPageshow) {
        window.removeEventListener('pageshow', this._stickyPageshow);
        this._stickyPageshow = null;
      }
    }
  }
  defineOnce('sticky-atc', StickyAtc);

  /* Remaining markup anchors with NO local implementation elsewhere.
     ⚠️ Do NOT add names here that a per-feature asset script already
     defines (media-gallery.js, product-modal.js, product-model.js,
     cart-notification.js, main-search.js, pickup-availability.js,
     facets.js, password-modal.js, quick-add.js, share.js, show-more.js,
     secondary.js) — those files register their own elements with an
     UNGUARDED customElements.define, so defining the name first makes
     them throw and die. The gallery, zoom and the rest of the long tail
     are owned by those local files, not by this one. */
  defineOnce('product-form');
  defineOnce('product-info');

  /* Mobile accordion motion: opening an answer changes layout once, then the
     affected rows travel from their old positions to their new positions via
     compositor transforms. Timing matches the cart and menu drawers. */
  if (!window.__restovaAccordionMotion && typeof Element.prototype.animate === 'function') {
    window.__restovaAccordionMotion = true;
    var accordionNarrow = window.matchMedia('(max-width: 749px)');
    var accordionReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    var accordionAnimations = new WeakMap();

    function syncAccordionMotionMode() {
      document.documentElement.classList.toggle(
        'accordion-flip-motion',
        accordionNarrow.matches && !accordionReduced.matches
      );
    }

    function listenForMediaChange(query) {
      if (typeof query.addEventListener === 'function') query.addEventListener('change', syncAccordionMotionMode);
      else if (typeof query.addListener === 'function') query.addListener(syncAccordionMotionMode);
    }

    function accordionMotion(row) {
      var styles = getComputedStyle(row);
      var raw = styles.getPropertyValue('--accordion-mobile-motion-duration').trim();
      var value = parseFloat(raw);
      var duration = value
        ? Math.max(120, Math.min(400, raw.slice(-2) === 'ms' ? value : value * 1000))
        : 250;
      return {
        duration: duration,
        easing: styles.getPropertyValue('--accordion-mobile-motion-ease').trim() || 'ease'
      };
    }

    function accordionMotionTargets(scope, rows, clickedRow) {
      var content = clickedRow.querySelector('.accordion__content-wrapper');
      var travel = content ? content.scrollHeight : 0;
      var clickedIndex = rows.indexOf(clickedRow);
      var openIndex = -1;
      for (var oi = 0; oi < rows.length; oi++) {
        if (rows[oi] !== clickedRow && rows[oi].querySelector('.accordion__details[open]')) {
          openIndex = oi;
          var openContent = rows[oi].querySelector('.accordion__content-wrapper');
          if (openContent) travel += openContent.scrollHeight;
          break;
        }
      }
      var firstAffected = clickedIndex + 1;
      if (openIndex >= 0 && openIndex < clickedIndex) firstAffected = openIndex + 1;
      var visibleLimit = window.innerHeight + travel + 100;
      var targets = [];

      // Rows above both toggles cannot move. Stop once the remaining rows are
      // beyond the area that can become visible during this open/close.
      for (var ri = firstAffected; ri < rows.length; ri++) {
        var rowRect = rows[ri].getBoundingClientRect();
        if (rowRect.top > visibleLimit) break;
        targets.push(rows[ri]);
      }

      var flowParent = scope.parentElement;
      if (!flowParent || flowParent.id !== 'MainContent') return targets;
      var sibling = scope.nextElementSibling;

      while (sibling) {
        var rect = sibling.getBoundingClientRect();
        if (rect.top > visibleLimit) break;
        var styles = getComputedStyle(sibling);
        if (
          styles.display !== 'none' &&
          styles.visibility !== 'hidden' &&
          styles.position !== 'fixed' &&
          (styles.transform === 'none' || accordionAnimations.has(sibling))
        ) {
          targets.push(sibling);
        }
        sibling = sibling.nextElementSibling;
      }
      return targets;
    }

    syncAccordionMotionMode();
    listenForMediaChange(accordionNarrow);
    listenForMediaChange(accordionReduced);

    document.addEventListener('click', function(event) {
      if (!document.documentElement.classList.contains('accordion-flip-motion')) return;
      var target = event.target;
      if (!target || !target.closest) return;
      var summary = target.closest('.accordion-v2 .accordion__summary');
      if (!summary) return;
      var clickedRow = summary.closest('.accordion-v2');
      if (!clickedRow) return;
      var scope = clickedRow.closest('.shopify-section') || clickedRow.parentElement;
      if (!scope) return;
      var rows = Array.prototype.slice.call(scope.querySelectorAll('.accordion-v2'));
      if (!rows.length) return;
      var targets = accordionMotionTargets(scope, rows, clickedRow);

      var before = targets.map(function(element) {
        var running = accordionAnimations.get(element);
        if (running) {
          var currentTransform = getComputedStyle(element).transform;
          element.style.transform = currentTransform === 'none' ? '' : currentTransform;
          running.cancel();
          accordionAnimations.delete(element);
        }
        return { element: element, top: element.getBoundingClientRect().top };
      });
      var motion = accordionMotion(clickedRow);

      requestAnimationFrame(function() {
        before.forEach(function(record) { record.element.style.transform = ''; });
        before.forEach(function(record) {
          var deltaY = record.top - record.element.getBoundingClientRect().top;
          if (Math.abs(deltaY) < 0.5) return;
          var animation = record.element.animate([
            { transform: 'translate3d(0,' + deltaY + 'px,0)' },
            { transform: 'translate3d(0,0,0)' }
          ], {
            duration: motion.duration,
            easing: motion.easing
          });
          accordionAnimations.set(record.element, animation);
          animation.onfinish = animation.oncancel = function() {
            if (accordionAnimations.get(record.element) === animation) accordionAnimations.delete(record.element);
          };
        });
      });
    }, true);
  }

  /* ── Shared media helper ─────────────────────────────────────────────
     Called by secondary.js when sliders/modals need playing media stopped. */
  if (typeof window.pauseAllMedia !== 'function') {
    window.pauseAllMedia = function () {
      document.querySelectorAll('.js-youtube').forEach(function (frame) {
        try {
          frame.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        } catch (_) {}
      });
      document.querySelectorAll('.js-vimeo').forEach(function (frame) {
        try {
          frame.contentWindow.postMessage('{"method":"pause"}', '*');
        } catch (_) {}
      });
      document.querySelectorAll('video').forEach(function (video) {
        try { video.pause(); } catch (_) {}
      });
      document.querySelectorAll('product-model').forEach(function (model) {
        try { if (model.modelViewerUI) model.modelViewerUI.pause(); } catch (_) {}
      });
    };
  }

})();
